import type { LibraryEntryResponse, ParameterLibraryType } from '@malaberg/shared';
/**
 * use-libraries hook — T23.
 *
 * Fetches library entries by type and scope. Results are cached in a Zustand
 * store keyed by `${type}:${scope}` to avoid duplicate API calls when the same
 * library is rendered in multiple components.
 *
 * Returns { entries, loading, error, refetch }.
 *
 * Design notes:
 * - Zustand selectors must return stable references to avoid "getSnapshot should
 *   be cached" warnings in React 18's useSyncExternalStore. Selectors that
 *   return `?? []` (new array each call) are NOT stable — always access the
 *   nested object by key and let callers fall back to the module-level constant.
 */
import { useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';
import { librariesApi } from '../api/endpoints/libraries';

// Stable empty array — returned by hook when cache has no entry for a key.
const EMPTY_ENTRIES: LibraryEntryResponse[] = [];

// ─── Cache store ──────────────────────────────────────────────────────────────

interface LibraryCacheState {
  entries: Record<string, LibraryEntryResponse[]>;
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
  setEntries: (key: string, items: LibraryEntryResponse[]) => void;
  setLoading: (key: string, value: boolean) => void;
  setError: (key: string, err: string | null) => void;
}

export const useLibraryCache = create<LibraryCacheState>((set) => ({
  entries: {},
  loading: {},
  errors: {},

  setEntries(key, items) {
    set((s) => ({ entries: { ...s.entries, [key]: items } }));
  },

  setLoading(key, value) {
    set((s) => ({ loading: { ...s.loading, [key]: value } }));
  },

  setError(key, err) {
    set((s) => ({ errors: { ...s.errors, [key]: err } }));
  },
}));

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseLibrariesResult {
  entries: LibraryEntryResponse[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useLibraries(type: ParameterLibraryType, scope: string | null): UseLibrariesResult {
  const cacheKey = scope ? `${type}:${scope}` : null;

  // Read from the Zustand records directly by key.
  // These selectors return the SAME array reference stored in the record,
  // so they are stable and won't trigger "getSnapshot should be cached".
  const entriesRecord = useLibraryCache((s) => s.entries);
  const loadingRecord = useLibraryCache((s) => s.loading);
  const errorsRecord = useLibraryCache((s) => s.errors);

  const entries = cacheKey ? (entriesRecord[cacheKey] ?? EMPTY_ENTRIES) : EMPTY_ENTRIES;
  const loading = cacheKey ? (loadingRecord[cacheKey] ?? false) : false;
  const error = cacheKey ? (errorsRecord[cacheKey] ?? null) : null;

  // Track whether we've initiated a fetch for the current key
  const hasFetchedRef = useRef<string | null>(null);

  const fetchData = useCallback(() => {
    if (!cacheKey || !scope) return;

    const { setEntries, setLoading, setError } = useLibraryCache.getState();
    setLoading(cacheKey, true);
    setError(cacheKey, null);

    librariesApi
      .list(type, { scope, status: 'active', limit: 200 })
      .then((res) => {
        const data = res as {
          ok: boolean;
          entries?: LibraryEntryResponse[];
          items?: LibraryEntryResponse[];
        };
        const list = data.entries ?? data.items ?? [];
        useLibraryCache.getState().setEntries(cacheKey, list);
        useLibraryCache.getState().setLoading(cacheKey, false);
      })
      .catch((err: unknown) => {
        useLibraryCache
          .getState()
          .setError(cacheKey, err instanceof Error ? err.message : 'Failed to load library');
        useLibraryCache.getState().setLoading(cacheKey, false);
      });
  }, [cacheKey, scope, type]);

  const refetch = useCallback(() => {
    if (cacheKey) {
      hasFetchedRef.current = null; // Allow re-fetch
    }
    fetchData();
  }, [cacheKey, fetchData]);

  useEffect(() => {
    if (!cacheKey) return;
    if (hasFetchedRef.current === cacheKey) return;

    hasFetchedRef.current = cacheKey;
    fetchData();
  }, [cacheKey, fetchData]);

  return {
    entries,
    loading,
    error,
    refetch,
  };
}
